import {useState, useCallback, useEffect, useMemo} from 'react';
import {message} from 'antd';
import {isEmpty} from 'underscore';
import moment from 'moment';
import {MarkerItem} from "../../../../types/home";
import useCartStore from "../../../../store/cartStore";
import useAuthStore from "../../../../store/authStore";
import useHomeStore from "../../../../store/homeStore";
import {DownloadInfoItem} from "../../../../types/analytics";
import {TFormat} from "../../../../utils/StaticVariable";
import {dateRangeLimit} from "../../../../utils/userGroupValidation";
import {getPointNameCartError} from "../component/Modal/Error/PointNameInCartError";
import useAddShoppingCartItems from "../../../../queries/useAddToCartMutation";
import {PayloadShoppingItems} from "../../../../types/shopping";
import {getCategoryFromRadioKey} from "../../../../utils/getCategoryDataset";
import useGetShoppingCartQuery from "../../../../queries/useGetShoppingCartQuery";
import { useIsFwe } from '../../../../hooks/useIsFwe';
import useDeleteShoppingItemMutation from '../../../../queries/useDeleteShoppingItemMutation';
import { tryFormatDate } from '../../../../utils/tryFormatDate';

export const useCartManagement = (
  activePoint: MarkerItem | null | undefined,
  setDatasetOfflineText: (item: string) => void,
  setDatasetIsOfflineWarningVisible: (item: boolean) => void,
) => {
  const shoppingItems = useCartStore(state => state.shoppingItems);
  const setShoppingItems = useCartStore(state => state.setShoppingItems);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const setShowLogin = useHomeStore(state => state.setShowLogin);

  const addItemToCartMutation = useAddShoppingCartItems();
  const deleteShoppingItemMutation = useDeleteShoppingItemMutation()

  const shouldShowRemoveButton = useMemo(() => {
    if (!activePoint?.label) return false
    
    const shoppingItemsOfPoint = shoppingItems?.[activePoint.label]
    if (!shoppingItemsOfPoint) return false
    
    return Object.keys(shoppingItemsOfPoint).length >= 3
  }, [activePoint?.label, shoppingItems])

  const shoppingCart = useGetShoppingCartQuery()
  const isFwe = useIsFwe(activePoint?.dataset)

  const [loadingCart, setLoadingCart] = useState<boolean>(false);
  const doAddProductToCart = useCallback(async (
    item: DownloadInfoItem | null,
    pointName: string,
    category: string,
    coordinates: number[]
  ): Promise<boolean> => {
    if (!item || isEmpty(item)) return false;

    const startDate = moment(item.dateRange?.[0]).format(TFormat);
    const endDate = moment(item.dateRange?.[1]).format(TFormat);
    const pointLabel = isFwe ? activePoint?.fwePointName ?? pointName : pointName

    const itemToAdd: PayloadShoppingItems[] = [{
      location: coordinates.join(', '),
      datasetName: item.datasetId,
      fileFormat: 'CSV',
      pointName: pointLabel,
      endDate,
      startDate,
      category
    }]

    try {
      await addItemToCartMutation.mutateAsync(itemToAdd);
      return true;
    } catch (error: any) {
      // Could be "Error: This point name has already been used."
      // When logging in after point creation, point names of current session might
      // conflict with point names in shopping cart of previous sessions
      const errorMessage = error.message || 'Failed to add the item to shopping cart';

      if (errorMessage.includes('offline')) {
        setDatasetOfflineText(errorMessage)
        setDatasetIsOfflineWarningVisible(true);

        return false
      }

      errorMessage === 'Error: This point name has already been used.'
        ? message.error(getPointNameCartError(pointName))
        : message.warn(errorMessage);

      return false;
    }
  }, [addItemToCartMutation]);

  useEffect(() => {
    const data = shoppingCart.data;

    if (data?.shoppingItems) {
      setShoppingItems(data?.shoppingItems);
    }
  }, [shoppingCart.data]);

  const addAllProductToCart = useCallback(async (
    items: PayloadShoppingItems[],
    shoppingItems: Record<string, any>
  ) => {
    setLoadingCart(true)
    const payloadItems: PayloadShoppingItems[] = [];

    for (const item of items) {
      const {
        pointName,
        category,
        location,
        startDate,
        endDate,
        datasetName,
        fileFormat,
      } = item;

      const pointWithSameNameIsInCard = pointName in shoppingItems;

      if (pointWithSameNameIsInCard) {
        const itemsPerPoint = shoppingItems[pointName];

        if (itemsPerPoint[category] !== undefined) continue;
      }

      const cartItem: PayloadShoppingItems = {
        pointName: pointName,
        category: category,
        datasetName: datasetName,
        startDate: startDate || '',
        endDate: endDate || '',
        location: location,
        fileFormat: fileFormat,
      };

      payloadItems.push(cartItem);
    }

    if (payloadItems.length === 0) {
      setLoadingCart(false);
      return;
    }

    try {
      addItemToCartMutation.mutate(payloadItems, {
        onSuccess: () => {
          message.success(`Success adding item to cart`);
          setLoadingCart(false)
        },
        onError: (error) => {
          console.error(`Error adding items to cart`, error);
          message.error(`Failed to add items to cart: ${error.message}`);
          setLoadingCart(false)
        }
      });
    } catch (error) {
      console.error(`Error while processing items for cart`, error);
      setLoadingCart(false);
    }
  }, [addItemToCartMutation, dateRangeLimit, getPointNameCartError, setLoadingCart]);

  const removeAllItemsFromPoint = async () => {
    if (!activePoint?.label) {
      message.warn('No active point to remove from cart');
      return
    }
  
    const shoppingItemsOfPoint = shoppingItems?.[activePoint.label];
    if (!shoppingItemsOfPoint) {
      message.warn('No items found for this point in cart');
      return
    }
  
    setLoadingCart(true)
    
    try {
      const categoriesInCart = Object.keys(shoppingItemsOfPoint)
      
      const deletePromises = categoriesInCart.map(async (category) => {
        const shoppingItem = shoppingItemsOfPoint[category]
        
        if (shoppingItem?.order_id) {
          const response = await deleteShoppingItemMutation.mutateAsync(shoppingItem.order_id)
          if (!response.Result || response.Result !== 'OK') {
            throw new Error(`Failed to delete ${category} from shopping cart`);
          }
        }
      });
      
      await Promise.all(deletePromises);
      
      message.success(`All items removed from cart for point ${activePoint.label}`)
    } catch (error: unknown) {      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      message.error(`Failed to remove items from cart: ${errorMessage}`)
    } finally {
      setLoadingCart(false)
    }
  }

    const handleAllToCart = async () => {
      if (!isAuthenticated()) {
        setShowLogin(true);
        return;
      }

      if (!activePoint) return;

      if (activePoint.label === '') {
        message.warn('Please give the active point a name');
        return;
      }

      const allActivePointItems = activePoint.downloadPage.map((item) => ({
        pointName: activePoint.label,
        category: getCategoryFromRadioKey(item.radioKey),
        datasetName: item.datasetId,
        startDate: tryFormatDate(item.dateRange?.[0], TFormat),
        endDate: tryFormatDate(item.dateRange?.[1], TFormat),
        location: activePoint.coordinates.join(','),
        fileFormat: 'CSV',
      }));
      
      if (allActivePointItems.length > 0) {
        await addAllProductToCart(allActivePointItems, shoppingItems);
      }
    };
  
  return {
    loadingCart,
    handleAllToCart,
    doAddProductToCart,
    getPointNameCartError,
    shouldShowRemoveButton,
    removeAllItemsFromPoint
  };
};
